import { useState } from 'react';
import { toast } from 'sonner';
import type { TaskDetail, TaskEvent, TaskMember, TaskUpdateInput } from '@shared/types/tasks';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { usePermission } from '@/hooks/usePermission';
import type { ApiResponse } from '@/lib/api-contract';
import { getDescriptionBefore, updateTask } from '@/lib/tasks-api';
import { isStaleWriteCode } from '@shared/types/stale-write';
import { MentionText } from './MentionText';
import { LinksOpenInDialogs } from '@/pages/link-dialog/link-scope';
import { touchTask } from './useTasks';

/**
 * T-50: a description change's old text, loaded when asked for, with Restore (for people who can edit
 * tasks). Restoring saves it against the description's current version, so a newer edit isn't lost.
 * T-59: `field="spec"` does the same for a spec change (its `spec_changed` event) and the task's spec.
 */
export function DescriptionBefore({
  task,
  event,
  members,
  onNavigate,
  field = 'description',
  load = (taskId, eventId) => getDescriptionBefore(taskId, eventId),
  save = (taskId, input) => updateTask(taskId, input),
}: {
  task: Pick<TaskDetail, 'id' | 'description' | 'description_updated_at' | 'spec' | 'spec_updated_at' | 'contexts'>;
  event: Pick<TaskEvent, 'id'>;
  members: TaskMember[];
  onNavigate?: () => void;
  /** Which text the change replaced: the description (a `described` event) or the spec (`spec_changed`). */
  field?: 'description' | 'spec';
  /** Preview only (the /dev/ui gallery): instead of GET …/description-before. */
  load?: (taskId: string, eventId: string) => Promise<ApiResponse<{ description: string }>>;
  /** Preview only: instead of PATCH /api/tasks/:id. */
  save?: (taskId: string, input: TaskUpdateInput) => Promise<ApiResponse<unknown>>;
}) {
  const canEdit = usePermission('TASKS_EDIT');
  const [open, setOpen] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const what = field === 'spec' ? 'spec' : 'description';
  const current = field === 'spec' ? task.spec : task.description;

  const toggle = async () => {
    if (open) return setOpen(false);
    setOpen(true);
    if (text !== null || loading) return;
    setLoading(true);
    const res = await load(task.id, event.id);
    setLoading(false);
    if (res.data) setText(res.data.description);
    else {
      setOpen(false);
      toast.error(`Couldn't load the ${what} before`, { description: res.error ?? undefined });
    }
  };

  const restore = async () => {
    if (text === null || restoring) return;
    setRestoring(true);
    const res = await save(
      task.id,
      field === 'spec'
        ? { spec: text, expected_spec_updated_at: task.spec_updated_at }
        : { description: text, expected_updated_at: task.description_updated_at }
    );
    setRestoring(false);
    if (res.error) {
      // Someone changed it since this tab last heard: reload (nothing here is unsaved), then Restore again works on theirs.
      if (isStaleWriteCode(res.code)) {
        touchTask(task.id);
        toast.error(`Couldn't restore that ${what}`, { description: `${res.error} The task has reloaded; press Restore again to put this version back over theirs.` });
      } else toast.error(`Couldn't restore that ${what}`, { description: res.error });
    } else {
      // Reload the task now rather than wait for the live socket's own tick (which may not come).
      touchTask(task.id);
      toast.success(`Restored that ${what}`);
      setOpen(false);
    }
  };

  return (
    <>
      <Button variant="link" size="xs" onClick={() => void toggle()} aria-expanded={open}>
        {open ? 'Hide the one before' : 'Show the one before'}
      </Button>
      {open ? (
        <div className="mt-1.5 rounded-md border bg-muted/30 p-2 text-sm text-foreground">
          {loading || text === null ? (
            <Spinner className="size-4" />
          ) : text.trim() ? (
            // SF-T-160: its links open in dialogs, as the task's comments do.
            <LinksOpenInDialogs>
              <MentionText text={text} members={members} contexts={task.contexts} onNavigate={onNavigate} markdown className="leading-relaxed" />
            </LinksOpenInDialogs>
          ) : (
            <p className="text-xs text-muted-foreground">It was empty.</p>
          )}
          {canEdit && text !== null && text !== current ? (
            <div className="mt-2 flex justify-end">
              <Button size="xs" variant="outline" loading={restoring} onClick={() => void restore()}>
                Restore this version
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

